import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  updateProvider,
  markProviderListStale,
  getProviderItem,
  handleClearFields,
  handleUpdateFields as handleUpdateProviderFields,
  getProviderItemLocale,
  updateProviderItemLocale,
} from "store/gameCenter/providers/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalProvidersTabs } from "configs/types";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import ProviderMainTab from "./tabs/ProviderMainTab";
import ProviderCurrenciesTab from "./tabs/ProviderCurrenciesTab";
import SeoTab from "../tabs/SeoTab";

import RestrictionTab from "../tabs/RestrictionTab";
import { Modal } from "components/Modal/Modal";
import styles from "./GameCenterProviderEditModal.module.scss";
import ProviderTrailTab from "./tabs/ProviderTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { isNumericOnlySlug } from "helpers/numericEntitySlug";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import {
  getSeoFieldErrors,
  getChangedSeoAttributes,
  normalizeSeoHtml,
} from "helpers/seoAttributes";
import LocalTab, { FieldUpdate as TlocaleFieldUpdate } from "../tabs/LocalTab";

const GameCenterProviderEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const permissions = usePermissionHelper();

  const payload = useAppSelector((store) => store.providers.payload);
  const itemFetched = useAppSelector((store) => store.providers.isItemFetched);
  const isItemFetching = useAppSelector(
    (store) => store.providers.isItemFetching,
  );
  const isItemLocaleFetching = useAppSelector(
    (store) => store.providers.isItemLocaleFetching,
  );

  const [tabs] = useState<ModalProvidersTabs[]>([
    ModalProvidersTabs.main,
    ModalProvidersTabs.currencies,
    ModalProvidersTabs.seo,
    ModalProvidersTabs.restriction,
    ModalProvidersTabs.locales,
    ModalProvidersTabs.trail,
  ]);
  const [activeTab, setActiveTab] = useState<ModalProvidersTabs>(
    ModalProvidersTabs.main,
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isTabLoading, setIsTabLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const [initialSeoData, setInitialSeoData] = useState<any>();
  const [dataRestriction, setDataRestriction] = useState<any>();

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const locales = useAppSelector((store) => store.settings.locales);
  const [locale, setLocale] = useState<string>(locales[0]);
  const translation = useAppSelector(
    (store) =>
      store.providers.payload?.translations?.find((v) => v.locale === locale) ??
      {},
  );

  useEffect(() => {
    if (entityId !== null) {
      dispatch(getProviderItem(entityId ?? 1));
    }
  }, [searchParams]);

  useEffect(() => {
    if (locales.length && !locale) {
      setLocale(locales[0]);
    }

    if (!entityId) return;

    if (activeTab !== ModalProvidersTabs.locales) return;

    let isCurrentRequest = true;

    setIsTabLoading(true);
    dispatch(getProviderItemLocale({ id: entityId, locale })).finally(() => {
      if (isCurrentRequest) {
        setIsTabLoading(false);
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [activeTab, dispatch, entityId, locale, locales]);

  const isLoaded = () => {
    if (entityId !== null && !itemFetched) {
      return false;
    }
    return true;
  };

  const loaded = isLoaded();

  const handleSetSeo = async () => {
    if (data) {
      setIsFetching(true);
      formErrors.resetErrors();
      try {
        const attributes = getChangedSeoAttributes(initialSeoData, data, [
          "headTitle",
          "metaTitle",
          "metaDescription",
          "ogTitle",
          "ogDescription",
          "twitterTitle",
          "twitterDescription",
          "description",
          "h1",
        ]);
        const fullDescription = getChangedSeoAttributes(
          initialSeoData,
          data,
          ["fullDescription"],
          { fullDescription: normalizeSeoHtml },
        );

        await instance.post(apiRoutes.setSeoAtrs, {
          instanceId: entityId,
          instanceType: "provider",
          locale: data.locale,
          attributes,
          ...fullDescription,
        });
        setInitialSeoData(data);
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        Object.entries(getSeoFieldErrors(error)).forEach(([field, code]) => {
          formErrors.setFieldError(field, code);
        });
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityUpdateError"),
            type: NotificationTypes.error,
          }),
        );
      } finally {
        setIsFetching(false);
      }
    }
  };

  const handleSetRestricion = async () => {
    if (dataRestriction) {
      setIsFetching(true);
      try {
        await instance.put(apiRoutes.setRestrictionsCountriesList, {
          instanceId: entityId,
          instanceType: "provider",
          geo: dataRestriction,
        });
        dispatch(markProviderListStale());
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityUpdateError"),
            type: NotificationTypes.error,
          }),
        );
      } finally {
        setIsFetching(false);
      }
    }
  };

  const handleApply = async () => {
    if (
      !permissions?.update ||
      activeTab === ModalProvidersTabs.trail ||
      isFetching ||
      isItemFetching
    ) {
      return;
    }

    if (activeTab === ModalProvidersTabs.main && payload) {
      formErrors.resetErrors();

      if (isNumericOnlySlug(payload.slug)) {
        formErrors.setFieldError("slug", "InvalidFormat");
        return;
      }

      try {
        await dispatch(updateProvider(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (activeTab === ModalProvidersTabs.seo && data) {
      handleSetSeo();
    }
    if (activeTab === ModalProvidersTabs.restriction && dataRestriction) {
      handleSetRestricion();
    }

    if (activeTab === ModalProvidersTabs.locales && entityId) {
      dispatch(updateProviderItemLocale({ id: entityId, locale: translation }));
    }
  };

  const isDisabled = () => {
    if (
      !!payload?.description &&
      !!payload.image &&
      !!payload.slug &&
      !!payload.title
    ) {
      return false;
    }
    if (!isFetching) {
      return false;
    }
    if (activeTab === ModalProvidersTabs.seo && data) {
      return false;
    }
    return true;
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleTabLoadingChange = useCallback((isLoading: boolean) => {
    setIsTabLoading(isLoading);
  }, []);

  const handleTabClick = (e: ModalProvidersTabs) => {
    setActiveTab(e);
    setIsTabLoading(
      e === ModalProvidersTabs.seo ||
        e === ModalProvidersTabs.restriction ||
        e === ModalProvidersTabs.locales,
    );
  };

  const handleChangeLocale = (e: TlocaleFieldUpdate, locale: string) => {
    const updatingProviderLocale = payload?.translations?.find(
      (l) => l.locale === locale,
    ) ?? { locale, title: "", description: "", isActive: false };

    const translationCategoryPrevious =
      payload?.translations?.filter((l) => l.locale !== locale) || [];
    dispatch(
      // @ts-ignore
      handleUpdateProviderFields({
        ...payload,
        translations: [
          ...translationCategoryPrevious,
          { ...updatingProviderLocale, [e.field]: e.value },
        ],
      }),
    );
  };

  const shouldShowFooter =
    permissions?.update &&
    activeTab !== ModalProvidersTabs.trail &&
    activeTab !== ModalProvidersTabs.currencies;
  const isReadOnly = !permissions?.update;

  const renderTab = () => {
    if (activeTab === ModalProvidersTabs.main) {
      return (
        <ProviderMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={isReadOnly}
        />
      );
    } else if (activeTab === ModalProvidersTabs.currencies) {
      return <ProviderCurrenciesTab isReadOnly={isReadOnly} />;
    } else if (activeTab === ModalProvidersTabs.seo) {
      return (
        <SeoTab
          currentData={data}
          onInitialData={setInitialSeoData}
          handleData={(e: any) => {
            setData(e);
          }}
          id={entityId ?? undefined}
          type="provider"
          isReadOnly={isReadOnly}
          onLoadingChange={handleTabLoadingChange}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
        />
      );
    } else if (activeTab === ModalProvidersTabs.restriction) {
      return (
        <RestrictionTab
          handleData={(e: any) => {
            setDataRestriction(e);
          }}
          id={entityId ?? undefined}
          type="provider"
          isReadOnly={isReadOnly}
          onLoadingChange={handleTabLoadingChange}
        />
      );
    } else if (activeTab === ModalProvidersTabs.trail) {
      return <ProviderTrailTab />;
    } else if (activeTab === ModalProvidersTabs.locales) {
      return (
        <LocalTab
          translation={translation}
          isLoaded={!isItemLocaleFetching}
          onChange={(v) => handleChangeLocale(v, locale)}
          locales={locales}
          activeLocale={locale}
          setActiveLocale={setLocale}
          readOnly={isItemFetching}
        />
      );
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("provider")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={`${styles.gameCenterProviderContentWrapper} ${
              activeTab === ModalProvidersTabs.currencies
                ? styles.currencyModalContent
                : ""
            } ${
              activeTab === ModalProvidersTabs.trail
                ? styles.trailContent
                : ""
            }`}
          >
            <div
              className={`game-form-wrapper ${
                activeTab === ModalProvidersTabs.seo ||
                activeTab === ModalProvidersTabs.locales
                  ? styles.withNestedTabs
                  : ""
              } ${
                activeTab === ModalProvidersTabs.currencies
                  ? styles.currencyTabContent
                  : ""
              }`}
            >
              {renderTab()}
            </div>
          </Modal.Content>

          {shouldShowFooter && (
            <Modal.Footer>
              {isTabLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    isPrimary
                    handleClick={handleApply}
                    txt={t("save")}
                    disabled={isDisabled()}
                    isLoad={isItemFetching || isFetching}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default GameCenterProviderEditModal;
